import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  Loader2,
  RefreshCw,
  Edit3,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { OtpInput } from "@/components/auth/OtpInput";
import {
  CloudflareTurnstile,
  type CloudflareTurnstileHandle,
} from "@/components/auth/CloudflareTurnstile";
import { useToast } from "@/hooks/use-toast";
import { onboardingService } from "@/services";
import { useEmailDomainValidation } from "@/hooks/use-email-domain-validation";
import { cn, friendlyError } from "@/lib/utils";

const VALID_EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

interface LeadContext {
  leadId: string;
  email: string;
  name: string;
  packageId: string | null;
  timezone: string;
  meetingLength: number;
  ndaRequired: boolean;
  summary: string;
}

function sanitizeEmail(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .split("")
    .filter((ch) => /^[a-zA-Z0-9@.\-_]$/.test(ch))
    .join("");
}

function getLeadContext(): LeadContext | null {
  try {
    const raw = localStorage.getItem("echopeak-lead-context");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.email !== "string") return null;
    parsed.email = sanitizeEmail(parsed.email);
    if (!VALID_EMAIL_REGEX.test(parsed.email)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export default function EmailVerificationPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [leadContext, setLeadContext] = useState<LeadContext | null>(null);
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<CloudflareTurnstileHandle>(null);
  const {
    status: domainStatus,
    errorMessage: domainError,
    isBlocking: domainIsBlocking,
    validateEmail: validateDomain,
    sanitizeEmail: hookSanitize,
    reset: resetDomainValidation,
  } = useEmailDomainValidation();

  useEffect(() => {
    const ctx = getLeadContext();
    if (!ctx) {
      navigate("/intake");
      return;
    }
    setLeadContext(ctx);
    setNewEmail(ctx.email);
  }, [navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const sendCode = useCallback(async () => {
    if (!leadContext) return;
    const isDevelopment = import.meta.env.VITE_ENVIRONMENT === "development";
    if (!turnstileToken && !isDevelopment) return;
    setIsSending(true);
    setError("");
    try {
      await onboardingService.sendEmailCode({
        email: leadContext.email,
        turnstileToken: turnstileToken || undefined,
      });
      setCodeSent(true);
      setResendCooldown(60);
      toast({
        title: "Verification code sent",
        description: `Check your inbox at ${leadContext.email}`,
      });
    } catch (err) {
      setError(friendlyError(err, "We couldn't send the code right now. Please try again in a moment."));
    } finally {
      setIsSending(false);
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    }
  }, [leadContext, toast, turnstileToken]);

  useEffect(() => {
    if (leadContext && !codeSent) {
      sendCode();
    }
  }, [leadContext, codeSent, sendCode]);

  const handleVerify = async (verifyCode: string) => {
    if (verifyCode.length !== 6 || !leadContext || isVerifying) return;
    setIsVerifying(true);
    setError("");
    try {
      await onboardingService.verifyEmailCode({
        email: leadContext.email,
        code: verifyCode,
      });

      localStorage.setItem(
        "echopeak-lead-context",
        JSON.stringify({ ...leadContext, emailVerified: true }),
      );
      if (leadContext.ndaRequired) {
        navigate("/verify/nda");
      } else {
        navigate("/engage");
      }
    } catch (err) {
      setError(friendlyError(err, "That code doesn't match. Please check and try again."));
      setCode("");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleEmailInputChange = (value: string) => {
    const sanitized = hookSanitize(value);
    setNewEmail(sanitized);
    validateDomain(sanitized);
  };

  const handleChangeEmail = () => {
    if (!leadContext) return;
    const sanitized = sanitizeEmail(newEmail);
    if (!VALID_EMAIL_REGEX.test(sanitized) || domainIsBlocking) return;
    const updated = { ...leadContext, email: sanitized };
    localStorage.setItem("echopeak-lead-context", JSON.stringify(updated));
    setLeadContext(updated);
    setEditingEmail(false);
    setCodeSent(false);
    setCode("");
    setError("");
    resetDomainValidation();
  };

  if (!leadContext) return null;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24">
      <div className="absolute inset-0 hero-gradient" />
      <div className="absolute inset-0 grid-background opacity-40" />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full relative z-10"
      >
        <Card className="glass-card border-gradient">
          <CardContent className="pt-8 pb-6 px-6 md:px-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              <h1 className="font-display text-2xl font-bold mb-2">
                Verify Your Email
              </h1>
              <p className="text-muted-foreground text-sm">
                We've sent a 6-digit verification code to protect calendar time
                and prevent spam.
              </p>
            </div>

            {editingEmail ? (
              <div className="space-y-3 mb-6">
                <div className="relative">
                  <Input
                    type="email"
                    value={newEmail}
                    onChange={(e) => handleEmailInputChange(e.target.value)}
                    placeholder="your@email.com"
                    autoFocus
                    maxLength={254}
                    className={cn(
                      "pr-10",
                      (domainStatus === "invalid-format" ||
                        domainStatus === "disposable" ||
                        domainStatus === "personal" ||
                        domainStatus === "error") &&
                        newEmail.length > 0 &&
                        "border-destructive/50 focus-visible:ring-destructive/30 focus-visible:border-destructive/50",
                      domainStatus === "valid" &&
                        "border-emerald-500/50 focus-visible:ring-emerald-500/30 focus-visible:border-emerald-500/50",
                    )}
                    title={
                      domainStatus === "invalid-format"
                        ? "Please enter a valid email address"
                        : domainStatus === "disposable" || domainStatus === "personal"
                          ? domainError || ""
                          : domainStatus === "error"
                            ? domainError || "Unable to verify domain"
                            : undefined
                    }
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    {domainStatus === "verifying" && (
                      <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                    )}
                    {domainStatus === "valid" && (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    )}
                    {(domainStatus === "invalid-format" ||
                      domainStatus === "disposable" ||
                      domainStatus === "personal" ||
                      domainStatus === "error") &&
                      newEmail.length > 0 && (
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingEmail(false);
                      setNewEmail(leadContext.email);
                      resetDomainValidation();
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleChangeEmail}
                    disabled={domainIsBlocking || !VALID_EMAIL_REGEX.test(newEmail)}
                    className="flex-1"
                  >
                    Update & Resend
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 mb-6 p-3 rounded-lg bg-primary/5 border border-primary/10">
                <span className="text-sm font-medium">{leadContext.email}</span>
                <button
                  onClick={() => setEditingEmail(true)}
                  className="text-primary hover:text-primary/80 transition-colors"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            <div className="space-y-6">
              <OtpInput
                value={code}
                onChange={setCode}
                onComplete={handleVerify}
                length={6}
                disabled={isVerifying}
                error={!!error}
                autoFocus
              />

              {isVerifying && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying...
                </div>
              )}

              {error && (
                <p className="text-sm text-destructive text-center">{error}</p>
              )}

              <div className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={sendCode}
                  disabled={resendCooldown > 0 || isSending}
                >
                  {isSending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  {resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : "Resend Code"}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                Code expires in 15 minutes. Maximum 5 sends per hour.
              </p>
            </div>

            <CloudflareTurnstile
              ref={turnstileRef}
              onVerify={setTurnstileToken}
              onExpire={() => setTurnstileToken(null)}
              onError={() => setTurnstileToken(null)}
              size="invisible"
            />
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
